/**
 * CustomPanesInitializer Component
 * Initialize custom Leaflet panes for z-index control
 *
 * Extracted from GeoTiffViewerEmbedded.tsx (lines 418-446, 29 lines)
 *
 * Features:
 * - Create custom panes with specific z-index values
 * - roofPolygonPane (z=350): Below default overlayPane but above tiles
 * - obstructionPane (z=450): Above overlayPane to ensure obstructions are clickable
 * - modulesPane (z=500): Above obstructions to ensure modules always visible on top
 * - Enable pointer events on all custom panes
 * - Force SVG renderer to update bounds on view changes (fixes polygon disappearing)
 */

import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

/**
 * Initialize custom Leaflet panes for layering control
 *
 * Creates three custom panes with specific z-index ordering:
 * - roofPolygonPane: z-index 350 (roof outlines below everything)
 * - obstructionPane: z-index 450 (obstructions above polygons)
 * - modulesPane: z-index 500 (modules always on top)
 *
 * Default Leaflet panes for reference:
 * - tilePane: 200
 * - overlayPane: 400
 * - shadowPane: 500
 * - markerPane: 600
 * - tooltipPane: 650
 * - popupPane: 700
 *
 * Usage:
 * ```tsx
 * <MapContainer>
 *   <CustomPanesInitializer />
 *   {other map components}
 * </MapContainer>
 * ```
 */
export function CustomPanesInitializer(): null {
  const map = useMap();

  useEffect(() => {
    // Create custom panes with specific z-index values
    // Default Leaflet panes: tilePane=200, overlayPane=400, shadowPane=500, markerPane=600, tooltipPane=650, popupPane=700

    if (!map.getPane('roofPolygonPane')) {
      const roofPane = map.createPane('roofPolygonPane');
      roofPane.style.zIndex = '350'; // Below default overlayPane (400) but above tiles (200)
      roofPane.style.pointerEvents = 'auto'; // Enable mouse events
    }

    if (!map.getPane('obstructionPane')) {
      const obstPane = map.createPane('obstructionPane');
      obstPane.style.zIndex = '450'; // Above overlayPane (400) to ensure obstructions are clickable
      obstPane.style.pointerEvents = 'auto'; // Enable mouse events
    }

    if (!map.getPane('modulesPane')) {
      const modulesPane = map.createPane('modulesPane');
      modulesPane.style.zIndex = '500'; // Above obstructions (450) to ensure modules always visible on top
      modulesPane.style.pointerEvents = 'auto'; // Enable mouse events
    }

    if (!map.getPane('segmentPane')) {
      const segmentPane = map.createPane('segmentPane');
      segmentPane.style.zIndex = '420'; // Above overlayPane (400) but below obstructions (450)
      segmentPane.style.pointerEvents = 'auto'; // Enable mouse events for selection
    }

    if (!map.getPane('segmentHandlesPane')) {
      const segmentHandlesPane = map.createPane('segmentHandlesPane');
      segmentHandlesPane.style.zIndex = '440'; // Above segmentPane (420) so handles are clickable over segment fill
      segmentHandlesPane.style.pointerEvents = 'auto'; // Enable mouse events for handles
    }

    // Force canvas/renderer to update after view changes
    // This fixes polygons disappearing when panning far from initial center
    const forceRedraw = () => {
      // Trigger a re-render by invalidating the map size
      // This causes Leaflet to recalculate all layer positions
      map.invalidateSize({ animate: false });
    };

    // Also fire a viewreset event to ensure all layers redraw
    const handleViewChange = () => {
      // Small delay to let the view settle
      setTimeout(() => {
        map.fire('viewreset');
      }, 10);
    };

    map.on('moveend', handleViewChange);
    map.on('zoomend', handleViewChange);

    return () => {
      map.off('moveend', handleViewChange);
      map.off('zoomend', handleViewChange);
    };
  }, [map]);

  return null;
}
